<script>
import { formatMinskDate } from '@/scripts/tools.js';
import PaginationController from '@/components/base-components/PaginationController.vue';
import PublisherManagementFilters from '@/components/page-provider-management/ProviderManagementFilters.vue';
import PublisherActionPopup from '@/components/page-provider-management/ProviderActionPopup.vue';
import ProviderTeamAccordion from '@/components/page-provider-management/ProviderTeamAccordion.vue';

const STATUS_STYLES = {
    active: {
        background: '#d1fae5',
        color: '#065f46',
        label: 'Active',
    },
    deleted: {
        background: '#fee2e2',
        color: '#991b1b',
        label: 'Deleted',
    },
    migrated: {
        background: '#f3f4f6',
        color: '#374151',
        label: 'Migrated',
    },
};

export default {
    name: 'ProviderManagementList',

    components: {
        PaginationController,
        PublisherManagementFilters,
        PublisherActionPopup,
        ProviderTeamAccordion,
    },

    data() {
        return {
            // Publisher currently targeted for an action popup
            editingPublisher: null,
            // 'delete' | 'migrate'
            editingMode: null,
            // { [publisher.id]: true } while visibility PATCH is in-flight
            visibilityLoading: {},
        };
    },

    computed: {
        publishers() {
            return this.$store.getters.ADMIN_PUBLISHERS;
        },
        isLoading() {
            return this.$store.getters.ADMIN_PUBLISHERS_LOADING;
        },
        isBadRequest() {
            return this.$store.getters.ADMIN_PUBLISHERS_BAD_REQUEST;
        },
        totalItems() {
            return this.$store.getters.ADMIN_PUBLISHERS_TOTAL;
        },

        // PaginationController uses 0-indexed currentPage and maxPage
        currentPageIndex() {
            return (this.$store.getters.ADMIN_PUBLISHERS_PAGE || 1) - 1;
        },
        maxPageIndex() {
            return (
                (this.$store.getters.ADMIN_PUBLISHERS_TOTAL_PAGES || 1) - 1
            );
        },

        perPage() {
            return this.$store.getters.ADMIN_PUBLISHERS_PER_PAGE;
        },

        displayFrom() {
            if (this.totalItems === 0) return 0;
            return this.currentPageIndex * this.perPage + 1;
        },
        displayTo() {
            return Math.min(
                (this.currentPageIndex + 1) * this.perPage,
                this.totalItems
            );
        },
    },

    methods: {
        // ── Date formatting ──────────────────────────────────────────────────
        formatDate(isoString) {
            return formatMinskDate(isoString, false);
        },

        // ── Status badge styles ──────────────────────────────────────────────
        statusStyle(status) {
            const cfg = STATUS_STYLES[status] || {
                background: '#f3f4f6',
                color: '#374151',
            };
            return {
                background: cfg.background,
                color: cfg.color,
                borderRadius: '4px',
                padding: '2px 10px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-block',
                letterSpacing: '0.3px',
            };
        },

        statusLabel(status) {
            return STATUS_STYLES[status]?.label || status || '—';
        },

        // ── Short ID display (first 8 chars) ─────────────────────────────────
        shortId(id) {
            if (!id) return '—';
            return String(id).length > 8
                ? String(id).substring(0, 8) + '…'
                : String(id);
        },

        // ── Visibility toggle ────────────────────────────────────────────────
        async toggleVisibility(publisher) {
            if (this.visibilityLoading[publisher.id]) return;
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderManagementList.toggleVisibility] provider:',
                    publisher.id,
                    'isPublic:',
                    !publisher.is_public
                );
            }
            this.visibilityLoading[publisher.id] = true;
            try {
                await this.$store.dispatch('updateProviderVisibility', {
                    id: publisher.id,
                    isPublic: !publisher.is_public,
                });
            } finally {
                this.visibilityLoading[publisher.id] = false;
            }
        },

        visibilityStyle(isPublic) {
            return isPublic
                ? {
                    background: '#d1fae5',
                    color: '#065f46',
                    borderRadius: '4px',
                    padding: '2px 10px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'inline-block',
                    letterSpacing: '0.3px',
                }
                : {
                    background: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '4px',
                    padding: '2px 10px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'inline-block',
                    letterSpacing: '0.3px',
                };
        },

        // ── Action popups ────────────────────────────────────────────────────
        openDeletePopup(publisher) {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderManagementList.openDeletePopup] Publisher:',
                    publisher.id
                );
            }
            this.editingPublisher = publisher;
            this.editingMode = 'delete';
        },

        openMigratePopup(publisher) {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderManagementList.openMigratePopup] Publisher:',
                    publisher.id
                );
            }
            this.editingPublisher = publisher;
            this.editingMode = 'migrate';
        },

        async closeActionPopup() {
            this.editingPublisher = null;
            this.editingMode = null;
            // Reload list to reflect changes
            await this.$store.dispatch('loadAdminPublishers');
        },

        // ── Pagination ───────────────────────────────────────────────────────
        async handleFirst() {
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
            await this.$store.dispatch('loadAdminPublishers');
        },
        async handleBack() {
            const page = this.$store.getters.ADMIN_PUBLISHERS_PAGE;
            if (page <= 1) return;
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', page - 1);
            await this.$store.dispatch('loadAdminPublishers');
        },
        async handleNext() {
            const page = this.$store.getters.ADMIN_PUBLISHERS_PAGE;
            const total = this.$store.getters.ADMIN_PUBLISHERS_TOTAL_PAGES;
            if (page >= total) return;
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', page + 1);
            await this.$store.dispatch('loadAdminPublishers');
        },
        async handleLast() {
            const total = this.$store.getters.ADMIN_PUBLISHERS_TOTAL_PAGES;
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', total);
            await this.$store.dispatch('loadAdminPublishers');
        },
        async changePerPage(event) {
            const value = parseInt(event.target.value, 10);
            this.$store.commit('SET_ADMIN_PUBLISHERS_PER_PAGE', value);
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
            await this.$store.dispatch('loadAdminPublishers');
        },
    },
};
</script>

<template>
    <div class="publisher_list">
        <!-- Filters bar -->
        <PublisherManagementFilters />

        <!-- Action popup (delete / migrate) -->
        <PublisherActionPopup
            v-if="editingPublisher && editingMode"
            :publisher="editingPublisher"
            :mode="editingMode"
            @close="closeActionPopup"
        />

        <!-- Summary bar -->
        <div class="list_summary">
            <span class="summary_count">
                <template v-if="isLoading">Loading…</template>
                <template v-else-if="totalItems > 0">
                    Showing {{ displayFrom }}–{{ displayTo }} of
                    {{ totalItems }} providers
                </template>
                <template v-else>No providers found</template>
            </span>

            <select
                class="per_page_select"
                :value="perPage"
                @change="changePerPage"
                title="Rows per page"
            >
                <option :value="10">10 / page</option>
                <option :value="20">20 / page</option>
                <option :value="50">50 / page</option>
            </select>
        </div>

        <!-- Table -->
        <div class="table_scroll">
            <!-- Loading skeleton -->
            <div v-if="isLoading" class="state_block state_loading">
                Loading providers…
            </div>

            <!-- Error state -->
            <div
                v-else-if="isBadRequest"
                class="state_block state_error"
            >
                Failed to load providers. Please try again.
            </div>

            <!-- Empty state -->
            <div
                v-else-if="publishers.length === 0"
                class="state_block state_empty"
            >
                No providers match your search criteria.
            </div>

            <!-- Data table -->
            <table v-else class="pub_table">
                <thead>
                    <tr>
                        <th class="col_id">ID</th>
                        <th class="col_name">Name / Email</th>
                        <th class="col_status">Status</th>
                        <th class="col_visibility">Visibility</th>
                        <th class="col_files">Files</th>
                        <th class="col_date">Created</th>
                        <th class="col_actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <template
                        v-for="publisher in publishers"
                        :key="publisher.id"
                    >
                    <tr
                        class="pub_row"
                    >
                        <!-- ID -->
                        <td class="col_id">
                            <span
                                class="id_chip"
                                :title="String(publisher.id)"
                            >
                                {{ shortId(publisher.id) }}
                            </span>
                        </td>

                        <!-- Name / Email -->
                        <td class="col_name">
                            <div class="name_block">
                                <span class="publisher_name">
                                    {{ publisher.name || '—' }}
                                </span>
                                <span
                                    v-if="publisher.email"
                                    class="publisher_email"
                                >
                                    {{ publisher.email }}
                                </span>
                            </div>
                        </td>

                        <!-- Status badge -->
                        <td class="col_status">
                            <span :style="statusStyle(publisher.provider_status)">
                                {{ statusLabel(publisher.provider_status) }}
                            </span>
                        </td>

                        <!-- Visibility toggle -->
                        <td class="col_visibility">
                            <div class="visibility_cell">
                                <span :style="visibilityStyle(publisher.is_public)">
                                    {{ publisher.is_public ? 'Public' : 'Hidden' }}
                                </span>
                                <button
                                    class="btn_visibility"
                                    :class="{ btn_loading: visibilityLoading[publisher.id] }"
                                    :disabled="!!visibilityLoading[publisher.id]"
                                    @click="toggleVisibility(publisher)"
                                    :title="publisher.is_public ? 'Make Hidden' : 'Make Public'"
                                >
                                    <template v-if="visibilityLoading[publisher.id]">…</template>
                                    <template v-else>{{ publisher.is_public ? 'Hide' : 'Publish' }}</template>
                                </button>
                            </div>
                        </td>

                        <!-- Files count -->
                        <td class="col_files">
                            {{ publisher.files_count !== undefined ? publisher.files_count : '—' }}
                        </td>

                        <!-- Registration date -->
                        <td class="col_date">
                            <span
                                v-if="publisher.created_at"
                                :title="publisher.created_at"
                            >
                                {{ formatDate(publisher.created_at) }}
                            </span>
                            <span v-else>—</span>
                        </td>

                        <!-- Actions -->
                        <td class="col_actions">
                            <div class="action_btns">
                                <button
                                    class="btn_migrate"
                                    @click="openMigratePopup(publisher)"
                                    title="Migrate publisher"
                                >
                                    Migrate
                                </button>
                                <button
                                    class="btn_delete"
                                    @click="openDeletePopup(publisher)"
                                    title="Delete publisher"
                                >
                                    Delete
                                </button>
                            </div>
                        </td>
                    </tr>
                    <!-- Team accordion expansion row -->
                    <tr class="team_expansion_row">
                        <td colspan="7" class="team_expansion_cell">
                            <ProviderTeamAccordion :provider="publisher" />
                        </td>
                    </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div
            v-if="!isLoading && publishers.length > 0"
            class="pagination_wrapper"
        >
            <PaginationController
                :currentPage="currentPageIndex"
                :maxPage="maxPageIndex"
                @first="handleFirst"
                @back="handleBack"
                @next="handleNext"
                @last="handleLast"
            />
        </div>
    </div>
</template>

<style scoped>
.publisher_list {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* Summary bar */
.list_summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.summary_count {
    font-size: 13px;
    color: var(--text-color-secondary);
}

.per_page_select {
    padding: 6px 10px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 13px;
    cursor: pointer;
}

/* Table scroll wrapper */
.table_scroll {
    overflow-x: auto;
    border: 1px solid var(--border-color);
    border-radius: 8px;
}

/* State blocks */
.state_block {
    padding: 40px 20px;
    text-align: center;
    font-size: 14px;
    color: var(--text-color-secondary);
}

.state_error {
    color: #ef4444;
}

/* Table */
.pub_table {
    width: 100%;
    border-collapse: collapse;
    min-width: 700px;
}

.pub_table thead tr {
    background: var(--background-secondary);
}

.pub_table th {
    padding: 10px 14px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-color-secondary);
    border-bottom: 1px solid var(--border-color);
    white-space: nowrap;
}

.pub_row {
    border-bottom: 1px solid var(--border-color);
    transition: background 0.15s;
}

.pub_row:last-child {
    border-bottom: none;
}

.pub_row:hover {
    background: var(--background-hover);
}

.pub_table td {
    padding: 12px 14px;
    font-size: 13px;
    color: var(--text-color);
    vertical-align: middle;
}

/* Column widths */
.col_id { width: 110px; }
.col_name { min-width: 180px; }
.col_status { width: 100px; }
.col_visibility { width: 190px; }
.col_files { width: 80px; text-align: right; }
.col_date { width: 130px; white-space: nowrap; }
.col_actions { width: 160px; }

/* ID chip */
.id_chip {
    font-family: monospace;
    font-size: 12px;
    color: var(--text-color-secondary);
    background: var(--background-secondary);
    padding: 2px 6px;
    border-radius: 4px;
    cursor: default;
}

/* Name block */
.name_block {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.publisher_name {
    font-weight: 600;
    font-size: 13px;
}

.publisher_email {
    font-size: 11px;
    color: var(--text-color-secondary);
    font-family: monospace;
}

/* Action buttons */
.action_btns {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}

.btn_migrate {
    padding: 5px 12px;
    border: 1px solid #d97706;
    border-radius: 5px;
    background: transparent;
    color: #d97706;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn_migrate:hover {
    background: #d97706;
    color: #fff;
}

.btn_delete {
    padding: 5px 12px;
    border: 1px solid #ef4444;
    border-radius: 5px;
    background: transparent;
    color: #ef4444;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn_delete:hover {
    background: #ef4444;
    color: #fff;
}

/* Visibility toggle */
.visibility_cell {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.btn_visibility {
    padding: 3px 10px;
    border: 1px solid #6b7280;
    border-radius: 5px;
    background: transparent;
    color: #6b7280;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn_visibility:hover:not(:disabled) {
    background: #6b7280;
    color: #fff;
}

.btn_visibility:disabled,
.btn_visibility.btn_loading {
    opacity: 0.55;
    cursor: not-allowed;
}

/* Pagination */
.pagination_wrapper {
    display: flex;
    justify-content: center;
    padding-top: 4px;
}

/* Team expansion row */
.team_expansion_row {
    border-bottom: 1px solid var(--border-color);
}

.team_expansion_cell {
    padding: 8px 16px 12px;
    background: var(--background-secondary);
}
</style>
